import { useId } from 'react';
import OperationSystemOptionListItem, {
    type OperationSystemOption,
} from '../../Atoms/OperationSystemOptionListItem/OperationSystemOptionListItem';

export type { OperationSystemOption };

interface OperationSystemOptionListProps {
    options: OperationSystemOption[];
    /** Accessible name of the radio group (visually hidden legend). */
    label: string;
    selectedOsSlug?: string;
    onSelectOs?: (os: OperationSystemOption) => void;
    selectedVersionSlug?: string | null;
    onSelectVersion?: (os: OperationSystemOption, versionSlug: string | null) => void;
    versionPlaceholder?: string;
    isLoading?: boolean;
}

export default function OperationSystemOptionList({
    options,
    label,
    selectedOsSlug,
    onSelectOs,
    selectedVersionSlug,
    onSelectVersion,
    versionPlaceholder,
    isLoading = false,
}: OperationSystemOptionListProps) {
    const radioName = useId();

    if (isLoading) {
        return (
            <div className="w-full h-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-full h-48 rounded-md bg-surface-3 animate-pulse" />
                ))}
            </div>
        );
    }

    if (!options || options.length === 0) {
        return (
            <div className="w-full h-auto p-8 rounded-md border-[0.5px] border-line bg-surface-1 flex items-center justify-center">
                <p className="text-fg/68">No operating systems available</p>
            </div>
        );
    }

    return (
        <fieldset className="w-full min-w-0">
            <legend className="sr-only">{label}</legend>
            <div className="w-full h-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {options.map((os) => (
                    <OperationSystemOptionListItem
                        key={os.slug}
                        os={os}
                        name={radioName}
                        isSelected={selectedOsSlug === os.slug}
                        onClick={() => onSelectOs?.(os)}
                        selectedVersionSlug={
                            selectedOsSlug === os.slug ? selectedVersionSlug : null
                        }
                        onSelectVersion={(versionSlug) => onSelectVersion?.(os, versionSlug)}
                        versionPlaceholder={versionPlaceholder}
                    />
                ))}
            </div>
        </fieldset>
    );
}
